/**
 * The view bar of the backlog: search, one Filter menu (kind, status, priority, severity,
 * label, owner, needs attention) whose active values show as removable chips, and saved views.
 * Everything is in the URL. The view switcher and anything else the page adds go in `end`.
 *
 * On a phone Filter sits beside the search and the views, project and view switch share the next
 * line: Filter and Views are icon-only there (their names stay the accessible names).
 */
import { Bookmark, BookmarkPlus, ChevronDown, ListFilter, Search, Trash2, X } from '@orgus/ui/icons';
import { useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode, Ref } from 'react';
import { ITEM_KINDS, ITEM_KIND_LABEL, ITEM_PRIORITIES, ITEM_PRIORITY_LABEL, ITEM_STATUSES, ITEM_STATUS_LABEL, SEVERITIES, SEVERITY_LABEL } from '@orgus/protocol';
import type { BacklogViewKind, ItemKind, ItemPriority, ItemStatus, SavedView, Severity } from '@orgus/protocol';
import {
  Button,
  Dialog,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  ErrorState,
  IconButton,
  Input,
  cn,
  toast,
} from '@orgus/ui';
import { toUiError } from '../../lib/api.ts';
import { NARROW_QUERY, useMediaQuery } from '../../lib/useMediaQuery.ts';
import type { UiError } from '../../lib/api.ts';
import { KindIcon } from '../missions/parts.tsx';
import { deleteView, saveView } from './data.ts';
import { filterCount } from './model.ts';
import type { ReqFilter } from './model.ts';

interface Facet<T extends string | number> {
  key: string;
  label: string;
  values: readonly T[] | undefined;
  options: readonly T[];
  render: (v: T) => ReactNode;
  word: (v: T) => string;
  onChange: (v: T[] | undefined) => void;
}

function FacetMenu<T extends string | number>({ f }: { f: Facet<T> }) {
  const set = new Set(f.values ?? []);
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <span className="flex-1">{f.label}</span>
        {set.size > 0 && <span className="pl-4 text-caption text-fg-subtle num">{set.size}</span>}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent>
        {f.options.map((o) => (
          <DropdownMenuCheckboxItem
            key={String(o)}
            checked={set.has(o)}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(c) => {
              const next = new Set(set);
              if (c) next.add(o);
              else next.delete(o);
              f.onChange(next.size ? f.options.filter((x) => next.has(x)) : undefined);
            }}
          >
            {f.render(o)}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

/** One active filter as a removable chip: "Status: Ready, In progress". */
function FilterChip({ label, text, onClear }: { label: string; text: string; onClear: () => void }) {
  return (
    <Button variant="secondary" size="sm" iconRight={<X />} onClick={onClear} aria-label={`Remove the ${label.toLowerCase()} filter: ${text}`} className="max-w-64">
      <span className="text-fg-subtle">{label}</span>
      <span className="truncate">{text}</span>
    </Button>
  );
}

export function FilterBar({
  filter,
  onChange,
  view,
  labels,
  owners,
  views,
  activeViewId,
  onApplyView,
  searchRef,
  builtinViews = [],
  leading,
  trailing,
  end,
}: {
  filter: ReqFilter;
  onChange: (f: ReqFilter) => void;
  view: BacklogViewKind;
  labels: string[];
  owners: string[];
  views: SavedView[];
  activeViewId: string | null;
  onApplyView: (v: SavedView) => void;
  searchRef?: Ref<HTMLInputElement>;
  /** Views the page always has (Read as a document, Defects), above the saved ones in the Views menu. */
  builtinViews?: { id: string; label: string; icon?: ReactNode; shortcut?: string[]; active?: boolean; onSelect: () => void }[];
  /** Controls before the search: the page's view switch (Backlog, Missions, Review). */
  leading?: ReactNode;
  /** A short fact after the filters: the match count. */
  trailing?: ReactNode;
  /** Controls at the end of the bar: the view switcher. */
  end?: ReactNode;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const phone = useMediaQuery(NARROW_QUERY);
  // Whether the page's own controls rendered anything: a fragment of nothing leaves the phone row to the view group.
  const trailingRef = useRef<HTMLSpanElement>(null);
  const [hasTrailing, setHasTrailing] = useState(false);
  useLayoutEffect(() => {
    const has = (trailingRef.current?.childElementCount ?? 0) > 0;
    setHasTrailing((h) => (h === has ? h : has));
  });
  const count = filterCount(filter);
  const set = (patch: Partial<ReqFilter>) => onChange({ ...filter, ...patch });
  const activeView = views.find((v) => v.id === activeViewId);
  const projectViews = views.filter((v) => !v.projectId || !filter.projectId || v.projectId === filter.projectId);

  const facets: Facet<string | number>[] = [];
  const add = <T extends string | number>(f: Facet<T>) => facets.push(f as unknown as Facet<string | number>);
  if (view !== 'defects') {
    add<ItemKind>({
      key: 'kind',
      label: 'Kind',
      values: filter.kinds,
      options: ITEM_KINDS,
      word: (k) => ITEM_KIND_LABEL[k],
      render: (k) => (
        <span className="flex items-center gap-2">
          <KindIcon kind={k} tooltip={false} />
          {ITEM_KIND_LABEL[k]}
        </span>
      ),
      onChange: (kinds) => set({ kinds }),
    });
  }
  add<ItemStatus>({ key: 'status', label: 'Status', values: filter.statuses, options: ITEM_STATUSES, word: (s) => ITEM_STATUS_LABEL[s], render: (s) => ITEM_STATUS_LABEL[s], onChange: (statuses) => set({ statuses }) });
  add<ItemPriority>({ key: 'priority', label: 'Priority', values: filter.priorities, options: ITEM_PRIORITIES, word: (p) => ITEM_PRIORITY_LABEL[p], render: (p) => ITEM_PRIORITY_LABEL[p], onChange: (priorities) => set({ priorities }) });
  if (view === 'defects' || filter.kinds?.includes('defect')) {
    add<Severity>({ key: 'severity', label: 'Severity', values: filter.severities, options: SEVERITIES, word: (s) => SEVERITY_LABEL[s], render: (s) => SEVERITY_LABEL[s], onChange: (severities) => set({ severities }) });
  }
  if (labels.length) add<string>({ key: 'label', label: 'Label', values: filter.labels, options: labels.slice(0, 30), word: (l) => l, render: (l) => l, onChange: (l) => set({ labels: l }) });
  if (owners.length) {
    add<string>({
      key: 'owner',
      label: 'Owner',
      values: filter.owner !== undefined ? [filter.owner ?? ''] : undefined,
      options: ['', ...owners],
      word: (o) => o || 'Unassigned',
      render: (o) => o || 'Unassigned',
      onChange: (o) => set({ owner: o?.length ? o[o.length - 1] || null : undefined }),
    });
  }
  const active = facets.filter((f) => f.values?.length);

  return (
    <div className="flex min-h-filter-bar flex-wrap items-center gap-2" role="toolbar" aria-label="Filters">
      {leading}
      <Input
        ref={searchRef}
        aria-label="Search items"
        size="sm"
        icon={<Search />}
        placeholder="Search by key, title, criteria"
        value={filter.q ?? ''}
        onChange={(e) => set({ q: e.target.value || undefined })}
        trailing={filter.q ? <IconButton label="Clear search" icon={<X />} size="xs" onClick={() => set({ q: undefined })} /> : undefined}
        wrapperClassName="min-w-0 grow basis-48 sm:w-56 sm:grow-0 sm:basis-auto"
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" icon={<ListFilter />}>
            <span className="max-sm:sr-only">Filter</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {facets.map((f) => (
            <FacetMenu key={f.key} f={f} />
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuCheckboxItem checked={!!filter.issues} onSelect={(e) => e.preventDefault()} onCheckedChange={(c) => set({ issues: c ? true : undefined })}>
            Needs attention
          </DropdownMenuCheckboxItem>
          {count > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem icon={<X />} onSelect={() => onChange({ projectId: filter.projectId })}>
                Clear filters
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {active.map((f) => (
        <FilterChip key={f.key} label={f.label} text={(f.values ?? []).map((v) => f.word(v)).join(', ')} onClear={() => f.onChange(undefined)} />
      ))}
      {filter.issues && <FilterChip label="Only" text="needs attention" onClear={() => set({ issues: undefined })} />}
      {count > 1 && (
        <Button variant="ghost" size="sm" onClick={() => onChange({ projectId: filter.projectId })}>
          Clear filters
        </Button>
      )}
      <span ref={trailingRef} className="contents">
        {trailing}
      </span>
      {/* On a phone Filter shares the search's line and the view group takes the next one whole (after the page's own controls when it adds some), never overlapping them. */}
      <div className={cn('ml-auto flex min-w-0 items-center justify-end gap-2 sm:flex-wrap', hasTrailing ? 'max-sm:min-w-fit' : 'max-sm:basis-full')}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" icon={<Bookmark />} iconRight={phone ? undefined : <ChevronDown />}>
              <span className="max-sm:sr-only">{activeView ? activeView.name : (builtinViews.find((b) => b.active)?.label ?? 'Views')}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {builtinViews.length > 0 && (
              <>
                {builtinViews.map((b) => (
                  <DropdownMenuItem key={b.id} icon={b.icon} shortcut={b.shortcut} onSelect={b.onSelect} className={b.active ? 'text-accent-text' : undefined}>
                    {b.label}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuLabel>Saved views</DropdownMenuLabel>
            {projectViews.length ? (
              projectViews.map((v) => (
                <DropdownMenuItem key={v.id} icon={<Bookmark />} onSelect={() => onApplyView(v)} className={v.id === activeViewId ? 'text-accent-text' : undefined}>
                  {v.name}
                </DropdownMenuItem>
              ))
            ) : (
              <p className="px-2 py-1 text-ui text-fg-subtle">No saved views yet.</p>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem icon={<BookmarkPlus />} onSelect={() => setSaving(true)}>
              Save this view…
            </DropdownMenuItem>
            {activeView && (
              <DropdownMenuItem
                icon={<Trash2 />}
                danger
                onSelect={async () => {
                  setError(null);
                  try {
                    await deleteView(activeView.id);
                    toast.success('View deleted', activeView.name);
                  } catch (e) {
                    setError(toUiError(e));
                  }
                }}
              >
                Delete "{activeView.name}"
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        {end}
      </div>
      {error && <ErrorState variant="inline" error={error} className="basis-full" action={<Button variant="ghost" size="sm" onClick={() => setError(null)}>Dismiss</Button>} />}
      <SaveViewDialog open={saving} onOpenChange={setSaving} filter={filter} view={view} initialName={activeView?.name ?? ''} />
    </div>
  );
}

function SaveViewDialog({ open, onOpenChange, filter, view, initialName }: { open: boolean; onOpenChange: (o: boolean) => void; filter: ReqFilter; view: BacklogViewKind; initialName: string }) {
  const [name, setName] = useState(initialName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<UiError | null>(null);
  const [touched, setTouched] = useState(false);
  const { projectId, ...rest } = filter;
  const submit = async () => {
    setTouched(true);
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const v = await saveView({ name: name.trim(), projectId: projectId ?? null, view, filter: rest });
      toast.success('View saved', v.name);
      onOpenChange(false);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (o) {
          setName(initialName);
          setTouched(false);
          setError(null);
        }
        onOpenChange(o);
      }}
      title="Save this view"
      description="Everyone on this hub sees it; an existing name is replaced."
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={submit}>
            Save view
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Input label="Name" placeholder="Defects to triage" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} autoFocus error={touched && !name.trim() ? 'Name the view.' : undefined} />
        {error && <ErrorState variant="inline" error={error} />}
      </form>
    </Dialog>
  );
}
